import React from 'react';
import { Link } from 'react-router-dom';
import { LegalLayout, LegalSection, COMPANY } from '../components/LegalLayout';

export const TermosPage = () => (
  <LegalLayout
    activeTab="terms"
    title="Termos de Uso"
    intro={`Estes Termos regem o uso da plataforma MeAdd, oferecida pela ${COMPANY.name}. Ao se cadastrar ou utilizar a plataforma, você concorda integralmente com estas condições.`}
    lastUpdated="07 de junho de 2026"
  >
    <LegalSection id="aceite" title="1. Aceitação dos Termos">
      <p>
        Ao criar uma conta, acessar ou utilizar qualquer recurso da plataforma MeAdd
        (&ldquo;Plataforma&rdquo;), você (&ldquo;Cliente&rdquo; ou &ldquo;Usuário&rdquo;) declara ter lido,
        compreendido e aceito integralmente estes Termos de Uso, bem como nossa{' '}
        <Link to="/privacidade" className="text-emerald-700 hover:underline">Política de Privacidade</Link>.
      </p>
      <p>
        Caso não concorde com qualquer disposição, não utilize a Plataforma.
      </p>
    </LegalSection>

    <LegalSection id="definicoes" title="2. Definições">
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Plataforma:</strong> sistema MeAdd disponibilizado em <code>meadd.app</code> e subdomínios.</li>
        <li><strong>Cliente:</strong> pessoa física ou jurídica que contrata o serviço (campanha política, partido, candidato, comitê).</li>
        <li><strong>Owner:</strong> usuário com permissão administrativa máxima no tenant do Cliente.</li>
        <li><strong>Apoiador:</strong> pessoa cadastrada voluntariamente pela campanha.</li>
        <li><strong>Liderança:</strong> pessoa que recebe um link/QR Code próprio para mobilizar apoiadores.</li>
        <li><strong>Tenant:</strong> ambiente isolado no qual o Cliente armazena seus dados.</li>
      </ul>
    </LegalSection>

    <LegalSection id="cadastro" title="3. Cadastro e elegibilidade">
      <p>Para utilizar a Plataforma, você deve:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Ter capacidade civil plena (18 anos ou mais)</li>
        <li>Fornecer informações verdadeiras, completas e atualizadas</li>
        <li>Manter a confidencialidade das credenciais de acesso</li>
        <li>Comunicar imediatamente o uso não autorizado da conta</li>
      </ul>
      <p>
        A {COMPANY.name} poderá recusar, suspender ou cancelar contas que apresentem
        irregularidades ou que descumpram estes Termos.
      </p>
    </LegalSection>

    <LegalSection id="trial-planos" title="4. Período de teste e planos">
      <p>
        Oferecemos um <strong>período de teste gratuito de 7 dias</strong> sem necessidade de
        cartão de crédito. Após esse prazo, o tenant entra em modo somente leitura até a
        contratação de um plano pago.
      </p>
      <p>Os planos atualmente disponíveis são:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Básico</strong> — R$ 499,00/mês</li>
        <li><strong>Pro</strong> — R$ 799,00/mês</li>
      </ul>
      <p>
        Os valores são <strong>cobrados mensalmente</strong> e poderão ser reajustados
        anualmente conforme variação do IPCA ou índice equivalente, com aviso prévio de 30 dias.
      </p>
      <p>
        O pagamento é processado pelo Stripe. Em caso de falha, concedemos 7 dias de tolerância
        antes de suspender o acesso a recursos pagos.
      </p>
    </LegalSection>

    <LegalSection id="cancelamento" title="5. Cancelamento e reembolso">
      <p>
        Você pode <strong>cancelar a assinatura a qualquer momento</strong> pelo portal de
        cobrança Stripe acessível em <code>/billing</code>. O cancelamento entra em vigor ao
        final do ciclo já pago — não há cobranças adicionais.
      </p>
      <p>
        Não há reembolso proporcional para períodos parcialmente utilizados, salvo
        determinação legal em contrário.
      </p>
      <p>
        Após o cancelamento, mantemos seus dados pelo prazo descrito na Política de Privacidade
        para que você possa exportá-los antes da exclusão definitiva.
      </p>
    </LegalSection>

    <LegalSection id="uso-aceitavel" title="6. Uso aceitável da Plataforma">
      <p>Ao utilizar o MeAdd, você se compromete a NÃO:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Realizar disparos em massa, spam ou comunicações sem consentimento</li>
        <li>Cadastrar dados de terceiros sem base legal (consentimento ou outra hipótese da LGPD)</li>
        <li>Utilizar a Plataforma para campanhas que violem a legislação eleitoral, a Constituição Federal ou normas do TSE</li>
        <li>Tentar acessar contas, tenants ou dados de outros usuários</li>
        <li>Realizar engenharia reversa, copiar ou redistribuir o software</li>
        <li>Sobrecarregar a infraestrutura com requisições automatizadas excessivas (scraping, DDoS)</li>
        <li>Inserir conteúdo ilegal, calunioso, discriminatório ou que incite violência</li>
      </ul>
      <p>
        O descumprimento poderá resultar em <strong>suspensão imediata</strong> sem reembolso
        e em comunicação às autoridades competentes.
      </p>
    </LegalSection>

    <LegalSection id="conteudo-cliente" title="7. Conteúdo do Cliente">
      <p>
        Todo conteúdo inserido pelo Cliente (cadastros, mensagens, imagens, comprovantes
        financeiros, etc.) permanece de sua propriedade. O Cliente concede à {COMPANY.name}
        licença <strong>não exclusiva, gratuita e limitada</strong> para armazenar, processar e
        exibir esse conteúdo exclusivamente para a operação da Plataforma.
      </p>
      <p>
        O Cliente é o <strong>único responsável</strong> pela licitude, exatidão e adequação do
        conteúdo inserido, incluindo o cumprimento da legislação eleitoral, LGPD, regras das
        plataformas de mensageria (WhatsApp, Telegram) e normas trabalhistas.
      </p>
    </LegalSection>

    <LegalSection id="ip" title="8. Propriedade intelectual">
      <p>
        Todos os direitos sobre a Plataforma (código, design, marca, logotipos, manuais,
        documentação) pertencem à {COMPANY.name}, salvo conteúdos inseridos pelos Clientes. É
        vedada qualquer reprodução, modificação ou distribuição sem autorização expressa.
      </p>
    </LegalSection>

    <LegalSection id="disponibilidade" title="9. Disponibilidade e suporte">
      <p>
        Empenhamo-nos para manter a Plataforma disponível 24/7, mas <strong>não garantimos
        disponibilidade ininterrupta</strong>. Janelas de manutenção, falhas de provedores
        externos ou eventos de força maior podem causar indisponibilidade temporária.
      </p>
      <p>
        Suporte técnico é prestado em horário comercial (segunda a sexta, 9h–18h, horário de
        Brasília) pelos canais oficiais.
      </p>
    </LegalSection>

    <LegalSection id="disclaimer" title="10. O que o MeAdd NÃO faz">
      <p className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-slate-800">
        O MeAdd é uma <strong>ferramenta de organização, mobilização e controle interno</strong>.
        A Plataforma <strong>não substitui</strong>:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Assessoria jurídica especializada em direito eleitoral</li>
        <li>Serviços contábeis ou de auditoria</li>
        <li>O sistema oficial de prestação de contas perante a Justiça Eleitoral</li>
        <li>Plataformas de envio de mensagens (WhatsApp, SMS, e-mail marketing)</li>
        <li>Sistemas oficiais de captação eleitoral ou plataformas governamentais</li>
      </ul>
      <p>
        Cabe ao Cliente, em conjunto com sua equipe jurídica e contábil, observar todas as
        regras aplicáveis à sua campanha.
      </p>
    </LegalSection>

    <LegalSection id="limitacao" title="11. Limitação de responsabilidade">
      <p>
        Na máxima extensão permitida pela legislação, a {COMPANY.name} não responde por:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Danos indiretos, lucros cessantes, perda de oportunidade ou de reputação</li>
        <li>Atos ou omissões de terceiros (apoiadores, lideranças, fornecedores)</li>
        <li>Consequências eleitorais, jurídicas ou contábeis decorrentes do uso da Plataforma</li>
        <li>Interrupções causadas por força maior, caso fortuito ou ataques cibernéticos</li>
      </ul>
      <p>
        A responsabilidade total da {COMPANY.name} em qualquer hipótese é limitada ao valor
        efetivamente pago pelo Cliente nos últimos 12 meses.
      </p>
    </LegalSection>

    <LegalSection id="indenizacao" title="12. Indenização">
      <p>
        O Cliente concorda em indenizar e isentar a {COMPANY.name}, seus sócios, administradores
        e colaboradores de qualquer reclamação, prejuízo ou despesa (incluindo honorários
        advocatícios) decorrente de:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Conteúdo inserido pelo Cliente que viole direitos de terceiros</li>
        <li>Descumprimento destes Termos ou da legislação aplicável</li>
        <li>Uso indevido ou abusivo da Plataforma</li>
      </ul>
    </LegalSection>

    <LegalSection id="modificacoes" title="13. Modificações dos Termos">
      <p>
        Estes Termos podem ser atualizados a qualquer momento. Mudanças relevantes serão
        comunicadas com antecedência mínima de 15 dias por e-mail e/ou aviso dentro da
        Plataforma. O uso continuado após o prazo equivale à aceitação tácita.
      </p>
    </LegalSection>

    <LegalSection id="lei" title="14. Lei aplicável e foro">
      <p>
        Estes Termos são regidos pela legislação brasileira. Para dirimir quaisquer
        controvérsias, fica eleito o <strong>foro da comarca de {COMPANY.city}/{COMPANY.state}</strong>,
        com renúncia expressa a qualquer outro, por mais privilegiado que seja.
      </p>
    </LegalSection>

    <LegalSection id="contato" title="15. Contato">
      <p>
        Dúvidas sobre estes Termos podem ser enviadas para{' '}
        <a className="text-emerald-700 hover:underline" href={`mailto:${COMPANY.contact_email}`}>
          {COMPANY.contact_email}
        </a>.
      </p>
    </LegalSection>
  </LegalLayout>
);

export default TermosPage;
